/**
 * Sound pips (08 §4): what a hearing player gets from the speakers, drawn - gunfire, shouts,
 * explosions and the Osprey as pips at the edge of the screen, on the bearing they come from.
 * ROUND8: a dog giving tongue is its own kind, drawn apart from a man's shout (08 §4 promises "dogs"), and so
 * is one growling as it closes on his trail (layer.ts).
 * What can be "heard" scales with `SimMods.hearingRadius`, which is where the Tinnitus trait
 * bites (04 §4). Pure: selection, distances and the edge-clamping maths.
 */

import type { SimEvent, SimMods } from '../../../sim/types';
import type { Point } from './projection';

export type PipKind = 'gunfire' | 'shout' | 'blast' | 'osprey' | 'dog';

/** Metres at which gunfire can still be placed by ear, before perks and traits. */
export const HEARING_BASE = 60;
/**
 * How far each kind carries, as a multiple of the base: a shout dies early, rotors cross the map. A dog's bark
 * carries further than a man's voice - it is played out to 80 m (sfx/dogs.ts) - and it is the one sound that
 * says a dog has him, so a player who cannot hear it must get it at least as far as the handler does (25 m).
 */
export const CARRY: Readonly<Record<PipKind, number>> = { gunfire: 1, shout: 0.5, blast: 1.5, osprey: 4, dog: 0.75 };
/** Words, not colours, tell the kinds apart. */
export const PIP_LABEL: Readonly<Record<PipKind, string>> = {
  gunfire: 'SHOTS', shout: 'VOICE', blast: 'BLAST', osprey: 'OSPREY', dog: 'DOG',
};
/**
 * What the eye already gives a player once the source is on screen: gunfire, a blast and the hovering Osprey are
 * plain to see there, so their pips show only from off screen. A voice is not, and nor is a dog's bark: at this
 * size a dog giving tongue is a few pixels of lifted head. Those two show on the source itself.
 */
export const PLAIN_TO_SEE: Readonly<Record<PipKind, boolean>> = {
  gunfire: true, shout: false, blast: true, osprey: true, dog: false,
};
/** A pip lingers this long after its last sound, ms. */
export const PIP_MS = 1400;
export const MAX_PIPS = 6;
/** Bearings are bucketed so a burst from one rifle is one pip, not thirty. */
const BUCKETS = 16;

/** How far away a sound of this kind can be placed, metres. */
export function hearingRange(kind: PipKind, mods: Pick<SimMods, 'hearingRadius'>): number {
  return HEARING_BASE * CARRY[kind] * Math.max(0, mods.hearingRadius);
}

/** The sound an event makes, if it is one the pips show. The ranger's own shots are not news to him. */
export function pipSource(ev: SimEvent): { kind: PipKind; x: number; z: number } | null {
  switch (ev.type) {
    case 'shot':
      return ev.fromPlayer ? null : { kind: 'gunfire', x: ev.x, z: ev.z };
    case 'bark':
      return { kind: ev.kind === 'dogBark' ? 'dog' : 'shout', x: ev.x, z: ev.z };
    case 'explosion':
    case 'mine':
      return { kind: 'blast', x: ev.x, z: ev.z };
    default:
      return null;
  }
}

/** 0..1: 1 on top of the listener, 0 at the edge of hearing or beyond. */
export function loudness(distance: number, range: number): number {
  return range <= 0 ? 0 : Math.min(1, Math.max(0, 1 - distance / range));
}

export interface PipPlace {
  x: number;
  y: number;
  /** Screen bearing from the listener to the sound, radians: 0 = right, PI / 2 = down. */
  angle: number;
  /** The sound is inside the inset frame: the pip sits on it rather than on the edge. */
  onScreen: boolean;
}

/**
 * Where a pip goes on a `width` x `height` screen: on the sound when that is inside the frame
 * inset by `inset`, else where the ray from the listener toward it leaves that frame. A listener
 * outside the frame is pulled to its centre so the ray always has an exit.
 */
export function placePip(listener: Point, source: Point, width: number, height: number, inset: number): PipPlace {
  const x0 = inset;
  const y0 = inset;
  const x1 = width - inset;
  const y1 = height - inset;
  const inside = (p: Point): boolean => p.x >= x0 && p.x <= x1 && p.y >= y0 && p.y <= y1;
  const from = inside(listener) ? listener : { x: width / 2, y: height / 2 };
  const dx = source.x - from.x;
  const dy = source.y - from.y;
  const angle = Math.atan2(dy, dx);
  if (inside(source)) return { x: source.x, y: source.y, angle, onScreen: true };
  // Smallest t at which from + t * d crosses a wall it is heading for.
  let t = 1;
  if (dx > 0) t = Math.min(t, (x1 - from.x) / dx);
  else if (dx < 0) t = Math.min(t, (x0 - from.x) / dx);
  if (dy > 0) t = Math.min(t, (y1 - from.y) / dy);
  else if (dy < 0) t = Math.min(t, (y0 - from.y) / dy);
  return { x: from.x + dx * t, y: from.y + dy * t, angle, onScreen: false };
}

/** Pips of one kind on nearly one bearing are the same pip. */
export function pipKey(kind: PipKind, angle: number): string {
  const bucket = ((Math.round(angle / ((Math.PI * 2) / BUCKETS)) % BUCKETS) + BUCKETS) % BUCKETS;
  return `${kind}:${bucket}`;
}

export interface Pip {
  key: string;
  kind: PipKind;
  place: PipPlace;
  /** 0..1, from distance. */
  loud: number;
  until: number;
}

/**
 * Add or refresh a pip. When the list is full the quietest goes, unless the newcomer is quieter
 * still. Returns the list it was given.
 */
export function addPip(pips: Pip[], pip: Pip): Pip[] {
  const same = pips.findIndex((p) => p.key === pip.key);
  if (same >= 0) {
    pips[same] = pip;
    return pips;
  }
  if (pips.length >= MAX_PIPS) {
    let quietest = 0;
    pips.forEach((p, i) => {
      if (p.loud < (pips[quietest]?.loud ?? 0)) quietest = i;
    });
    if ((pips[quietest]?.loud ?? 0) >= pip.loud) return pips;
    pips.splice(quietest, 1);
  }
  pips.push(pip);
  return pips;
}
